import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { RealtimeChannel } from '@supabase/supabase-js';
import { logger } from '@/lib/logger';

const HOOK_NAME = 'useGlobalRealtime';

// Per-table debounce times in ms (high-frequency tables get longer debounce)
const TABLE_DEBOUNCE_MS: Record<string, number> = {
  sales: 800,
  weight_sales: 800,
  stock_movements: 800,
  goals: 300,
  ingredients: 500,
  daily_productions: 500,
  rodizio_productions: 500,
  product_losses: 500,
  post_production_losses: 500,
  combos: 300,
  notifications: 300,
  recipes: 300,
  menu_items: 300,
  financial_movements: 500,
  referrals: 300,
  subscriptions: 500,
  support_tickets: 300,
  support_ticket_messages: 300,
};

const DEFAULT_DEBOUNCE_MS = 300;

// Table to query key mapping
const TABLE_INVALIDATION_MAP: Record<string, string[]> = {
  sales: ['sales', 'unified_sales_regular', 'unified_sales_weight', 'dashboard-data', 'global-kpis', 'dashboard', 'dashboard-kpis', 'balance-sheet-receivables', 'dre-receivables-start', 'dre-receivables-end'],
  weight_sales: ['weight_sales', 'unified_sales_weight', 'unified_sales_regular', 'dashboard-data', 'global-kpis', 'dashboard', 'dashboard-kpis', 'balance-sheet-receivables'],
  ingredients: ['ingredients', 'stock-overview', 'stock-movements-today', 'low-stock-items', 'inventory-report', 'recipes', 'menu-items', 'cmv-impact', 'balance-sheet-inventory', 'price-review-report'],
  stock_movements: ['stock-overview', 'stock-movements-today', 'stock_movements', 'ingredients', 'low-stock-items', 'inventory-report', 'dashboard-data', 'global-kpis'],
  custom_categories: ['ingredient-categories', 'custom-categories'],
  recipes: ['recipes', 'menu-items', 'dashboard-data', 'cmv-impact', 'price-review-report', 'combos', 'global-kpis'],
  menu_items: ['menu-items', 'dashboard-data', 'combos', 'global-kpis'],
  combos: ['combos', 'menu-items', 'dashboard-data', 'global-kpis'],
  notifications: ['notifications'],
  daily_productions: ['pending-productions', 'all-pending-productions', 'production-history', 'daily_productions', 'completed-productions'],
  rodizio_productions: ['pending-productions', 'all-pending-productions', 'production-history', 'rodizio_productions', 'completed-productions'],
  product_losses: ['product_losses', 'product-losses', 'losses-analysis', 'dre-product-losses', 'dashboard-data', 'global-kpis'],
  post_production_losses: ['post-production-losses', 'losses-analysis', 'dre-product-losses', 'dashboard-data', 'global-kpis'],
  service_fees: ['service-fees', 'restaurant-costs', 'dashboard-data', 'global-kpis'],
  payment_fees: ['payment-fees', 'restaurant-costs', 'dashboard-data', 'global-kpis'],
  tax_settings: ['taxes', 'restaurant-costs', 'dashboard-data', 'global-kpis'],
  fixed_costs: ['fixed-costs', 'fixed-costs-list', 'dashboard-data', 'global-kpis'],
  goals: ['goals', 'dashboard-data', 'global-kpis'],
  financial_movements: [
    'financial-movements',
    'balance-sheet-payables',
    'balance-sheet-inventory',
    'balance-sheet-receivables',
    'dre-payables-start', 'dre-payables-end',
    'dre-fixed-costs-detail',
    'dashboard-data', 'global-kpis',
  ],
  referrals: ['referrals', 'referral-code'],
  partner_referrals: ['referral-partners', 'referrals'],
  subscriptions: ['subscription', 'user-stores'],
  support_tickets: ['my-tickets', 'support-tickets'],
  support_ticket_messages: ['my-tickets'],
};

/**
 * Centralized Realtime hook that consolidates all Postgres subscriptions
 * into a single channel per restaurant.
 * 
 * Benefits:
 * - Reduces connection overhead (1 channel vs 5+ individual)
 * - Debounces invalidations to prevent cache thrashing
 * - Smart invalidation based on affected tables
 */
export const useGlobalRealtime = (restaurantId: string | undefined) => {
  const queryClient = useQueryClient();
  const channelRef = useRef<RealtimeChannel | null>(null);
  const debounceTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Debounced invalidation to prevent multiple rapid invalidations
  // Uses per-table debounce and surgical restaurantId-scoped invalidation
  const debouncedInvalidate = useCallback((queryKeys: string[], table: string) => {
    const debounceMs = TABLE_DEBOUNCE_MS[table] || DEFAULT_DEBOUNCE_MS;
    
    queryKeys.forEach((key) => {
      // Clear existing timer for this key
      const existingTimer = debounceTimersRef.current.get(key);
      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      // Set new debounced invalidation
      const timer = setTimeout(() => {
        logger.debug(HOOK_NAME, `Invalidating query: ${key} (debounce: ${debounceMs}ms)`);
        // Surgical: invalidate scoped to active restaurantId
        if (restaurantId) {
          queryClient.invalidateQueries({ queryKey: [key, restaurantId] });
        }
        // Also invalidate root key (for queries without restaurantId like dashboard-data)
        queryClient.invalidateQueries({ queryKey: [key] });
        debounceTimersRef.current.delete(key);
      }, debounceMs);

      debounceTimersRef.current.set(key, timer);
    });
  }, [queryClient, restaurantId]);

  // Handle realtime payload
  const handleRealtimeChange = useCallback((payload: {
    eventType: string;
    table: string;
    new?: Record<string, unknown>;
    old?: Record<string, unknown>;
  }) => {
    const { table, eventType } = payload;
    
    logger.info(HOOK_NAME, `Realtime change detected`, {
      table,
      event: eventType,
    });

    // Get query keys to invalidate based on table
    const keysToInvalidate = TABLE_INVALIDATION_MAP[table] || [];
    
    if (keysToInvalidate.length > 0) {
      debouncedInvalidate(keysToInvalidate, table);
    }
  }, [debouncedInvalidate]);

  useEffect(() => {
    if (!restaurantId) {
      logger.debug(HOOK_NAME, 'No restaurantId, skipping subscription');
      return;
    }

    // Prevent duplicate subscriptions
    if (channelRef.current) {
      logger.debug(HOOK_NAME, 'Channel already exists, skipping');
      return;
    }

    logger.info(HOOK_NAME, 'Creating global realtime channel', { restaurantId });

    // Create a single channel for all tables
    const channel = supabase
      .channel(`global-realtime-${restaurantId}`)
      // Sales changes
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'sales',
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => handleRealtimeChange({ ...payload, table: 'sales' })
      )
      // Weight sales changes
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'weight_sales',
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => handleRealtimeChange({ ...payload, table: 'weight_sales' })
      )
      // Ingredients changes
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'ingredients',
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => handleRealtimeChange({ ...payload, table: 'ingredients' })
      )
      // Stock movements
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'stock_movements',
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => handleRealtimeChange({ ...payload, table: 'stock_movements' })
      )
      // Notifications
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => handleRealtimeChange({ ...payload, table: 'notifications' })
      )
      // Daily productions
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'daily_productions',
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => handleRealtimeChange({ ...payload, table: 'daily_productions' })
      )
      // Rodizio productions
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'rodizio_productions',
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => handleRealtimeChange({ ...payload, table: 'rodizio_productions' })
      )
      // Product losses
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'product_losses',
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => handleRealtimeChange({ ...payload, table: 'product_losses' })
      )
      // Post-production losses
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'post_production_losses',
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => handleRealtimeChange({ ...payload, table: 'post_production_losses' })
      )
      // Service fees (delivery platform rates)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'service_fees',
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => handleRealtimeChange({ ...payload, table: 'service_fees' })
      )
      // Payment fees (card machine rates)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'payment_fees',
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => handleRealtimeChange({ ...payload, table: 'payment_fees' })
      )
      // Fixed costs
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'fixed_costs',
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => handleRealtimeChange({ ...payload, table: 'fixed_costs' })
      )
      // Financial movements (Gestão Financeira)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'financial_movements',
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => handleRealtimeChange({ ...payload, table: 'financial_movements' })
      )
      // Recipes (ficha técnica changes)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'recipes',
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => handleRealtimeChange({ ...payload, table: 'recipes' })
      )
      // Menu items (cardápio changes)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'menu_items',
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => handleRealtimeChange({ ...payload, table: 'menu_items' })
      )
      // Combos
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'combos',
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => handleRealtimeChange({ ...payload, table: 'combos' })
      )
      // Custom categories (ingredient + menu)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'custom_categories',
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => handleRealtimeChange({ ...payload, table: 'custom_categories' })
      )
      // Tax settings
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tax_settings',
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => handleRealtimeChange({ ...payload, table: 'tax_settings' })
      )
      // Goals
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'goals',
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => handleRealtimeChange({ ...payload, table: 'goals' })
      )
      // Referrals (Programa de Indicações)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'referrals',
          filter: `referrer_restaurant_id=eq.${restaurantId}`,
        },
        (payload) => handleRealtimeChange({ ...payload, table: 'referrals' })
      )
      // Subscriptions (Assinatura - aprovações, extensões)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'subscriptions',
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => handleRealtimeChange({ ...payload, table: 'subscriptions' })
      )
      // Support tickets
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'support_tickets',
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => handleRealtimeChange({ ...payload, table: 'support_tickets' })
      )
      // Support ticket messages (INSERT only - new responses)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'support_ticket_messages',
        },
        (payload) => handleRealtimeChange({ ...payload, table: 'support_ticket_messages' })
      )
      .subscribe((status) => {
        logger.info(HOOK_NAME, 'Channel subscription status', { status });
      });

    channelRef.current = channel;

    // Cleanup
    return () => {
      logger.info(HOOK_NAME, 'Cleaning up global realtime channel');
      
      // Clear all debounce timers
      debounceTimersRef.current.forEach((timer) => clearTimeout(timer));
      debounceTimersRef.current.clear();

      // Unsubscribe from channel
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [restaurantId, handleRealtimeChange]);

  // Return nothing - this hook is side-effect only
  return null;
};
